import React from 'react';
import { connect } from 'react-redux';
import {
	TagOutlined,
	PlusOutlined,
	EditOutlined,
	SearchOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import {
	Button,
	Drawer,
	Input,
	List,
	Tooltip,
	Modal,
	message,
	Spin,
	ConfigProvider,
	Empty,
} from 'antd';
import { isEqual } from 'lodash';
import { Label, LabelEntityType, CreateLabelRequest, UpdateLabelRequest } from '../../types/labels';
import { labelsOnPartsRestCrudThunksForSupplier } from '../../thunks/labels_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { RootState } from '../../reducers/root_reducer';
import { withUserType } from '../withUserType';
import { ColorPicker } from './ColorPicker';
import { generateRandomColor, generateTempLabelId } from '../../constants/labelConstants';
import PaginatedReduxList from '../paginated_redux_list/PaginatedReduxList';
import LabelDisplay from '../LabelDisplay';
import { EmptyState } from '../empty_state/EmptyState';

type LabelCreationOwnProps = {
	targetCollectionName: LabelEntityType;
};

type LabelCreationProps = LabelCreationOwnProps & {
	userType?: string;
	labels: Label[];
	loading: boolean;
	createLabel: (label: CreateLabelRequest) => Promise<any>;
	updateLabel: (label: Label & UpdateLabelRequest) => Promise<any>;
	deleteLabel: (labelId: number) => Promise<any>;
};

function LabelCreation({
	labels,
	loading,
	createLabel,
	updateLabel,
	deleteLabel,
	targetCollectionName,
}: LabelCreationProps): React.ReactElement {
	const [drawerOpen, setDrawerOpen] = React.useState(false);
	const [isEditing, setIsEditing] = React.useState(false);
	const [searchText, setSearchText] = React.useState('');
	const [draftLabels, setDraftLabels] = React.useState<Label[]>([]);
	const [editingLabelId, setEditingLabelId] = React.useState<number | null>(null);
	const [showSearchInput, setShowSearchInput] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);
	const [searchFilters, setSearchFilters] = React.useState({});

	const hasUnsavedChanges = React.useMemo(() => {
		if (!isEditing) return false;
		return !isEqual(labels, draftLabels);
	}, [labels, draftLabels, isEditing]);

	const filteredLabels = isEditing ? draftLabels : labels;

	React.useEffect(() => {
		if (!isEditing && drawerOpen) {
			const timer = setTimeout(() => {
				const trimmedSearch = searchText.trim();
				setSearchFilters(trimmedSearch ? { search: trimmedSearch } : {});
			}, 300);
			return () => clearTimeout(timer);
		}
	}, [searchText, isEditing, drawerOpen]);

	const openDrawer = () => {
		setDrawerOpen(true);
	};

	const resetAllState = () => {
		setIsEditing(false);
		setDraftLabels([]);
		setEditingLabelId(null);
		setSearchText('');
		setShowSearchInput(false);
		setSearchFilters({});
	};

	const showDiscardConfirmation = (onDiscard: () => void) => {
		Modal.confirm({
			title: 'Discard changes?',
			content: 'Your unsaved changes will be lost.',
			okText: 'Keep Editing',
			cancelText: 'Discard Changes',
			onCancel: onDiscard,
		});
	};

	const requestClose = () => {
		if (isEditing && hasUnsavedChanges) {
			showDiscardConfirmation(() => {
				setDrawerOpen(false);
				resetAllState();
			});
			return;
		}
		setDrawerOpen(false);
		resetAllState();
	};

	const startEditing = () => {
		setDraftLabels([...labels]);
		setIsEditing(true);
	};

	const exitEditMode = () => {
		setIsEditing(false);
		setDraftLabels([]);
		setEditingLabelId(null);
	};

	const cancelEditing = () => {
		if (hasUnsavedChanges) {
			showDiscardConfirmation(exitEditMode);
			return;
		}
		exitEditMode();
	};

	const saveEdits = async () => {
		for (const label of draftLabels) {
			if (!label.label.trim()) {
				message.warning('Please fill in all labels or remove empty ones');
				return;
			}
		}

		setIsSaving(true);
		try {
			const newLabels = draftLabels.filter((label) => label.id < 0);

			const deletedLabels = labels.filter((label) => !draftLabels.find((dl) => dl.id === label.id));

			const modifiedLabels = draftLabels.filter((draftLabel) => {
				if (draftLabel.id < 0) return false;
				const originalLabel = labels.find((l) => l.id === draftLabel.id);
				if (!originalLabel) return false;
				return originalLabel.label !== draftLabel.label || originalLabel.color !== draftLabel.color;
			});

			const operations: Promise<any>[] = [];

			newLabels.forEach((label) => {
				const createRequest: CreateLabelRequest = {
					label: label.label,
					color: label.color,
				};
				operations.push(createLabel(createRequest));
			});

			modifiedLabels.forEach((label) => {
				operations.push(
					updateLabel({
						id: label.id,
						label: label.label,
						color: label.color,
					})
				);
			});

			deletedLabels.forEach((label) => {
				operations.push(deleteLabel(label.id));
			});

			await Promise.all(operations);

			setSearchText('');
			setShowSearchInput(false);
			setSearchFilters({});
			exitEditMode();
			message.success('Labels saved successfully');
		} catch (error) {
			console.error('Error saving labels:', error);
			message.error(error || 'Failed to save labels');
		} finally {
			setIsSaving(false);
		}
	};

	const addNewLabel = () => {
		const tempLabel: Label = {
			id: generateTempLabelId(),
			label: '',
			color: generateRandomColor(),
		};
		setDraftLabels((prev) => [...prev, tempLabel]);
	};

	const handleDeleteLabel = (labelId: number) => {
		setDraftLabels((prev) => prev.filter((l) => l.id !== labelId));
	};

	const handleUpdateLabelColor = (labelId: number, color: string) => {
		setDraftLabels((prev) => prev.map((l) => (l.id === labelId ? { ...l, color } : l)));
		setEditingLabelId(null);
	};

	const renderLabelItem = (item: Label) => (
		<div key={item.id} className="pb-2">
			<LabelDisplay className="w-full" tag={item} showassociatedPartsCount />
		</div>
	);

	return (
		<div>
			<Button size="large" icon={<TagOutlined />} onClick={openDrawer}>
				Labels
			</Button>
			<Drawer
				title={
					<div className="flex w-full items-center justify-start">
						<TagOutlined />
						<span className="h5Medium ml-2">{isEditing ? 'Edit Labels' : 'Labels'}</span>
					</div>
				}
				visible={drawerOpen}
				width={420}
				onClose={requestClose}
				extra={
					!isEditing ? (
						<div className="flex w-full items-center justify-end gap-x-3">
							{showSearchInput ? (
								<Input
									allowClear
									size="small"
									placeholder="Search labels..."
									prefix={<SearchOutlined />}
									value={searchText}
									onChange={(e) => setSearchText(e.target.value)}
									onBlur={() => {
										if (!searchText.trim()) {
											setShowSearchInput(false);
										}
									}}
									autoFocus
									className="w-50"
								/>
							) : (
								<Tooltip title="Search Labels">
									<Button
										type="text"
										icon={<SearchOutlined />}
										onClick={() => setShowSearchInput(true)}
									/>
								</Tooltip>
							)}
							<Tooltip title="Edit Labels">
								<Button type="text" icon={<EditOutlined />} onClick={startEditing} />
							</Tooltip>
						</div>
					) : null
				}
			>
				{!isEditing ? (
					// @ts-ignore - Type inference issue with Redux connected component
					<ConfigProvider
						renderEmpty={() => (
							<EmptyState body="No labels yet. Click edit to create labels" height="auto" />
						)}
					>
						<PaginatedReduxList
							collectionName="labels"
							targetCollectionName={targetCollectionName}
							listItemRenderFunction={renderLabelItem}
							fetchData={labelsOnPartsRestCrudThunksForSupplier.read}
							initialPagination={{ current: 1, pageSize: 10 }}
							initialSorting={{ sort_by: 'label', order: 'asc' }}
							initialFilters={{}}
							additionalParams={searchFilters}
							itemLayout="horizontal"
						/>
					</ConfigProvider>
				) : (
					<Spin spinning={loading}>
						<div>
							{filteredLabels.length === 0 ? (
								<div className="bodyregular pb-5 text-center text-gray-500">No labels yet</div>
							) : (
								<List
									rowKey="id"
									dataSource={filteredLabels}
									renderItem={(item) => {
										const isNewLabel = item.id < 0;
										return (
											<List.Item className="px-0 py-2">
												<div className="flex w-full items-center gap-x-3">
													<ColorPicker
														currentColor={item.color}
														onColorChange={(color) => handleUpdateLabelColor(item.id, color)}
														onCancel={() => setEditingLabelId(null)}
														visible={editingLabelId === item.id}
														onVisibleChange={(visible) =>
															setEditingLabelId(visible ? item.id : null)
														}
													/>

													<Input
														value={item.label}
														onChange={(e) =>
															setDraftLabels((prev) =>
																prev.map((label) =>
																	label.id === item.id ? { ...label, label: e.target.value } : label
																)
															)
														}
														className="mr-2 flex-1"
														placeholder="Label name"
														size="small"
														autoFocus={isNewLabel}
													/>

													<Button
														icon={<DeleteOutlined />}
														onClick={() => handleDeleteLabel(item.id)}
													/>
												</div>
											</List.Item>
										);
									}}
								/>
							)}

							<Button icon={<PlusOutlined />} onClick={addNewLabel} type="link">
								Add Label
							</Button>

							<div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4">
								<Button onClick={cancelEditing} disabled={isSaving}>
									Cancel
								</Button>
								<Button
									type="primary"
									onClick={saveEdits}
									disabled={!hasUnsavedChanges || isSaving}
									loading={isSaving}
								>
									Save
								</Button>
							</div>
						</div>
					</Spin>
				)}
			</Drawer>
		</div>
	);
}

const mapStateToProps = (
	state: RootState,
	ownProps: LabelCreationOwnProps & { userType: string }
) => ({
	labels: getRecordsForTargetCollection(state.labels, ownProps.targetCollectionName) || [],
	loading:
		state.labels &&
		state.labels[ownProps.targetCollectionName] &&
		state.labels[ownProps.targetCollectionName].fetching,
});

const mapDispatchToProps = (
	dispatch: any,
	ownProps: LabelCreationOwnProps & { userType: string }
) => ({
	createLabel: (label: CreateLabelRequest) =>
		dispatch(labelsOnPartsRestCrudThunksForSupplier.create(label, ownProps.targetCollectionName)),
	updateLabel: (label: Label & UpdateLabelRequest) =>
		dispatch(labelsOnPartsRestCrudThunksForSupplier.update(label)),
	deleteLabel: (labelId: number) =>
		dispatch(labelsOnPartsRestCrudThunksForSupplier.delete({ id: labelId }, 'id')),
});

// @ts-ignore - Type inference issue with HOC composition, same pattern works in LabelSelector
export default withUserType(connect(mapStateToProps, mapDispatchToProps)(LabelCreation));
